import { formatIDR } from "./format.js";
import type { Accessory, Desk, DeskSize, DeskSpec, ProjectDesk, Stat } from "./types.js";

/**
 * Catalogue data for the storefront.
 *
 * Prices are from the Kanata Indonesia Customer Catalogue 2026. They are
 * duplicated here for the prerendered pages — the API seeded from the same
 * numbers is the authority at checkout, and the cart total always comes from
 * the server. If the two ever disagree, the server wins and this file is wrong.
 *
 * Copy is Bahasa Indonesia, kamu. English stays only where the industry term is
 * English: standing desk, setup, motor, workspace.
 */

/** Shared Karya specs — single motor, 75–120cm, 80kg, 20mm/sec. */
export const karyaSpec: DeskSpec = {
  heightRange: "75–120 cm",
  load: "80 kg",
  speed: "20 mm/detik",
  motor: "Single motor elektrik",
};

export const karyaSizes: readonly DeskSize[] = ["120", "140", "160"];

/** Tabletop finishes, as the catalogue spells them. */
export const karyaColorways = ["Brown", "Walnut", "Beige", "White"] as const;

/**
 * Frame finishes.
 *
 * Deliberately not the tabletop set. White/Black/Silver is the standing-desk
 * industry default, and two pickers offering the same four swatches reads as a
 * rendering fault more often than as a choice — a shopper seeing "Walnut" under
 * both headings assumes the page has repeated itself.
 *
 * No surcharge: price varies by size alone. That is what makes a
 * generate-the-matrix admin action viable instead of 36 hand-entered prices per
 * desk, and it is the assumption to check first if a colourway ever costs more.
 */
export const karyaLegColorways = ["White", "Black", "Silver"] as const;

/** The 160 cm top costs more; 120 and 140 are the same price. */
export const size160Surcharge = 200_000;

export const karyaSizeNote = "Harga sama untuk 120 dan 140 cm. Yang 160 cm tambah Rp 200.000.";

const karyaSpecRows = [
  { label: "Tinggi", value: "75–120 cm" },
  { label: "Beban", value: "80 kg" },
  { label: "Kecepatan", value: "20 mm/detik" },
  { label: "Motor", value: "Single motor elektrik" },
  { label: "Ukuran meja", value: "120 / 140 / 160 × 70 cm" },
];

/** Karya — the desks you can buy here. */
export const desks: Desk[] = [
  {
    slug: "karya-polos",
    name: "Karya Polos",
    tagline: "Yang kamu butuhkan, tanpa yang tidak.",
    description:
      "Rangka elektrik single motor dan tabletop kayu keras tahan air dan panas. Tidak ada colokan, tidak ada dudukan monitor — kalau setup kamu sudah punya semuanya, ini yang kamu perlu.",
    price: { amount: 2_999_000 },
    sizeSurcharge: size160Surcharge,
    imageKey: "karya-polos",
    highlights: ["Single motor elektrik", "Tabletop tahan air dan panas", "3 ukuran, 4 warna meja, 3 warna kaki"],
    specs: karyaSpecRows,
    colors: karyaColorways,
    legColors: karyaLegColorways,
  },
  {
    slug: "karya-klasik",
    name: "Karya Klasik",
    tagline: "Colokan dan USB sudah menyatu di mejanya.",
    description:
      "Semua yang ada di Polos, plus stop kontak dan port USB yang menyatu di meja. Kabel charger, monitor dan lampu berhenti di meja, tidak lagi turun ke lantai.",
    price: { amount: 3_525_000 },
    sizeSurcharge: size160Surcharge,
    imageKey: "karya-klasik",
    highlights: ["Stop kontak terintegrasi", "Port USB bawaan", "Paling banyak dipilih"],
    specs: [...karyaSpecRows, { label: "Tambahan", value: "Stop kontak + port USB terintegrasi" }],
    colors: karyaColorways,
    legColors: karyaLegColorways,
    featured: true,
  },
  {
    slug: "karya-plus",
    name: "Karya Plus",
    tagline: "Meja, colokan, dan dudukan monitor jadi satu.",
    description:
      "Karya Klasik ditambah dudukan monitor bawaan, jadi layar sudah di tinggi mata sejak hari pertama. Satu paket, tidak perlu beli aksesori terpisah.",
    price: { amount: 3_825_000 },
    sizeSurcharge: size160Surcharge,
    imageKey: "karya-plus",
    highlights: ["Stop kontak dan USB", "Dudukan monitor bawaan", "Langsung lengkap"],
    specs: [...karyaSpecRows, { label: "Tambahan", value: "Stop kontak, port USB, dudukan monitor" }],
    colors: karyaColorways,
    legColors: karyaLegColorways,
  },
];

/**
 * Project desks. Consultation only — no price, no cart.
 *
 * They live here so the storefront can show what Kanata makes without implying
 * you can buy one in two clicks. The CTA is WhatsApp.
 */
export const projectDesks: ProjectDesk[] = [
  {
    slug: "daya",
    name: "Daya",
    type: "Dual motor",
    tagline: "Lebih kuat, lebih cepat, jangkauan lebih panjang.",
    description:
      "Dua motor untuk beban lebih berat dan gerak lebih cepat: 60–125 cm, 120 kg, 32 mm/detik. Untuk workspace yang dipakai sepanjang hari.",
    imageKey: "daya",
    specs: { heightRange: "60–125 cm", load: "120 kg", speed: "32 mm/detik", motor: "Dual motor" },
  },
  {
    slug: "hadap",
    name: "Hadap",
    type: "Back to back",
    tagline: "Dua orang, satu meja, tinggi masing-masing.",
    description:
      "Meja berhadapan dengan pengaturan tinggi terpisah di tiap sisi. Hemat tempat, dan tidak ada yang harus ikut tinggi orang lain.",
    imageKey: "hadap",
  },
  {
    slug: "siku",
    name: "Siku",
    type: "L shape",
    tagline: "Sudut ruangan jadi meja kerja penuh.",
    description:
      "Bentuk L untuk kerja dengan banyak layar atau banyak berkas sekaligus. Tinggi tetap bisa diatur, rangkanya tetap kokoh.",
    imageKey: "siku",
  },
];

/** Aksesori. Semua bisa dibeli di sini. */
export const accessories: Accessory[] = [
  {
    slug: "raya",
    name: "Raya Monitor Stand",
    tagline: "Layar naik ke tinggi mata.",
    description:
      "Dudukan monitor 110 × 11 × 22 cm. Layar naik ke tinggi mata, dan ruang di bawahnya cukup untuk keyboard waktu meja sedang tidak dipakai.",
    price: { amount: 369_000 },
    imageKey: "raya",
    specs: [{ label: "Ukuran", value: "110 × 11 × 22 cm" }],
    colors: ["Brown", "Walnut", "White", "Black", "Beige"],
  },
  {
    slug: "rapih",
    name: "Rapih Cable Tray",
    tagline: "Kabel naik ke bawah meja.",
    description:
      "Baja anti-karat 1 mm dengan powder coating matte hitam. 50 × 10 cm, tinggi 8,5 cm. Disekrup ke bawah meja di dua titik.",
    price: { amount: 290_000 },
    imageKey: "rapih",
    specs: [
      { label: "Ukuran", value: "50 × 10 cm, tinggi 8,5 cm" },
      { label: "Material", value: "Baja anti-karat 1 mm, powder coating" },
      { label: "Pemasangan", value: "Disekrup ke bawah meja (2 titik)" },
    ],
  },
  {
    slug: "alur",
    name: "Alur Cable Chain",
    tagline: "Kabel ikut waktu meja naik-turun.",
    description:
      "Rantai kabel lentur yang melindungi dan merapikan kabel sepanjang meja bergerak. Pemasangannya gampang.",
    price: { amount: 179_000 },
    imageKey: "alur",
    colors: ["White", "Black"],
  },
  {
    slug: "sinar",
    name: "Sinar Screen Bar LED",
    tagline: "Meja terang, layar tidak memantul.",
    description:
      "Panjang 410 mm dengan remote nirkabel, tiga suhu cahaya dan brightness yang bisa diatur. Cahayanya jatuh ke meja, bukan ke layar.",
    // The client's October 2026 price sheet: Rp 449.000, struck through from
    // Rp 549.000. Supersedes the catalogue's Rp 499.000.
    price: { amount: 449_000 },
    imageKey: "sinar",
    specs: [
      { label: "Panjang", value: "410 mm" },
      { label: "Suhu cahaya", value: "6400K / 4200K / 3100K" },
      { label: "Daya", value: "5W maks, DC5V, 1000mA" },
      { label: "LED", value: "84 titik" },
    ],
  },
  {
    slug: "putar",
    name: "Putar Table Wheels",
    tagline: "Meja bisa pindah, lalu dikunci.",
    description:
      "Roda dengan kunci di tiap kaki, dibuat untuk rangka Karya. Meja bisa digeser kapan saja, lalu dikunci di tempatnya. Satu set isi 4 roda.",
    price: { amount: 124_900 },
    imageKey: "putar",
    specs: [{ label: "Isi", value: "1 set berisi 4 roda" }],
  },
];

/**
 * TODO(divine): these numbers conflicted between catalog.ts (3000+ desks, 20+
 * clients, 4.9/5) and the live site (150+ clients, 15 years). Neither set is
 * confirmed, and inventing a third would be worse than showing none — so the
 * array is empty and the storefront renders no stat band until somebody says
 * which is true.
 */
export const stats: Stat[] = [];

export const trustIndustries = ["Elektronik", "Medis", "Keuangan", "Pendidikan"];

/**
 * TODO(divine): real, attributed customer quotes. The previous placeholders
 * were written in-house and presented as customer voices, which is the kind of
 * thing that is only ever found out at the worst moment. Empty until real ones
 * exist.
 */
export const testimonials: never[] = [];

/** The three-step service model for project/office work. */
export const officeProcess = [
  {
    step: "01",
    title: "Konsultasi",
    description: "Kami bahas kebutuhan kantor kamu dan susun solusinya bareng.",
  },
  {
    step: "02",
    title: "Produksi",
    description: "Dibuat di Indonesia, sesuai ukuran dan spesifikasi yang disepakati.",
  },
  {
    step: "03",
    title: "Pemasangan",
    description: "Dikirim, dipasang, dan dites di tempat. Sudah termasuk.",
  },
];

/**
 * Where Kanata's team installs, and roughly what it costs.
 *
 * Estimates, not prices: the real fee is agreed over WhatsApp once a technician
 * is free, and it is never added to the order total. Outside these zones there
 * is no installation yet.
 */
export const pasangZones = [
  { zone: "Zona 1", areas: ["Jakarta Barat"], estimateIdr: 250_000 },
  {
    zone: "Zona 2",
    areas: ["Jakarta Selatan", "Jakarta Pusat", "Jakarta Utara", "Jakarta Timur"],
    estimateIdr: 350_000,
  },
  { zone: "Zona 3", areas: ["Tangerang", "Tangerang Selatan", "Depok", "Bekasi"], estimateIdr: 500_000 },
] as const;

/** The cheapest zone, for "mulai Rp …" copy. */
export const pasangFromIdr = Math.min(...pasangZones.map((z) => z.estimateIdr));

/** What pasang means, in the customer's words. Used on PDP and checkout. */
export const pasangCopy = {
  label: "Pemasangan di tempat",
  /** Short fee hint beside the label. */
  fee: `Mulai ${formatIDR(pasangFromIdr)}`,
  description:
    "Tim kami datang, rakit mejanya, dan pastikan semuanya jalan sebelum pulang. Biayanya tergantung zona dan dikonfirmasi lewat WhatsApp.",
  optOut: "Saya rakit sendiri",
  /** The checkout summary row: the fee is agreed later, never in this total. */
  checkoutValue: "Dikonfirmasi via WhatsApp",
  /** Shown before a customer counts on a technician: pasang is booked by hand. */
  notice: {
    title: "Pemasangan di tempat",
    body: "Pemasangan di tempat membutuhkan konfirmasi terlebih dahulu melalui Whatsapp karena kami perlu mengecek ketersediaan teknisi. Anda bisa kontak kami langsung melalui Whatsapp dan menanyakan tentang hal ini.",
    tableCaption: "Estimasi biaya pemasangan",
    tableHeaders: { zone: "Zona", areas: "Wilayah", estimate: "Estimasi biaya" },
    footnote:
      "Biaya di atas masih estimasi dan belum termasuk dalam total pesanan. Untuk rincian lengkapnya, hubungi kami lewat WhatsApp terlebih dahulu. Saat ini pemasangan baru tersedia di tiga zona tersebut.",
    whatsapp: "Tanya via WhatsApp",
    dismiss: "Jangan tampilkan ini lagi",
    ok: "Mengerti",
    continue: "Lanjut ke pembayaran",
  },
} as const;
